// src/pages/Admin.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import QuoteForm from '../components/QuoteForm';
import DemocraticFrameworkTab from '../components/DemocraticFrameworkTab';
import PolitiFactScraper from '../components/PolitiFactScraper';
import ArticleAdminTab from '../components/ArticleAdminTab';
import { Edit, Trash2, ChevronDown, ChevronUp, Check, X, AlertCircle, CheckCircle, History } from 'lucide-react';

const Admin = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // UI state
  const [activeTab, setActiveTab] = useState(() => {
    // Check localStorage for a saved tab preference
    const savedTab = localStorage.getItem('adminActiveTab');
    return savedTab || 'add';
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedQuoteId, setExpandedQuoteId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Data state
  const [quotes, setQuotes] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tagUsage, setTagUsage] = useState({});
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  
  // State variables for the component
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(50); // Fixed at 50 items per page
  const [totalQuotes, setTotalQuotes] = useState(0);

  // New quote form state - now using the updated schema
  const [newQuote, setNewQuote] = useState({
    claim_text: '',
    claim_source: '',
    claim_date: new Date().toISOString().split('T')[0],
    reality_text: '',
    reality_sources: '[]',
    veracity_rating: '',
    citation: '',
    video_url: '',
    tags: ''
  });

  // State to track login process
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  // Default username - hardcoded for now
  const [username, setUsername] = useState('admin');

  // Initial loading state to prevent flash of login screen
  const [authLoading, setAuthLoading] = useState(true);
  
  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('Checking authentication status...');
        
        // Use the token-based method only
        console.log('Checking token-based authentication...');
        const { AUTH_CONFIG } = await import('../config');
        const isAuth = await AUTH_CONFIG.isAuthenticated();
        console.log('Auth check result:', isAuth);
        
        setIsAuthenticated(isAuth);
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadData(1); // Start with page 1 when first loading
    }
  }, [isAuthenticated]);
  
  // Save the active tab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('adminActiveTab', activeTab);
  }, [activeTab]);

  const loadData = async (page = currentPage) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get quotes with pagination
      const quotesResponse = await fetch(`/api/admin/quotes?page=${page}&pageSize=${pageSize}`);
      if (!quotesResponse.ok) {
        throw new Error('Failed to fetch quotes');
      }
      const quotesData = await quotesResponse.json();
      
      // The API returns an object with quotes property and pagination metadata
      const quotesArray = quotesData.quotes || [];
      
      // Update pagination state
      if (quotesData.pagination) {
        setTotalPages(quotesData.pagination.totalPages || 1);
        setCurrentPage(quotesData.pagination.currentPage || 1);
        // Don't update pageSize since it's fixed at 50
        setTotalQuotes(quotesData.pagination.totalQuotes || 0);
      }
      
      // Ensure quotes is always an array
      setQuotes(Array.isArray(quotesArray) ? quotesArray : []);
      
      // Get tags
      const tagsResponse = await fetch('/api/admin/tags');
      if (!tagsResponse.ok) {
        throw new Error('Failed to fetch tags');
      }
      const tagsData = await tagsResponse.json();
      setAllTags(tagsData.tags);
      setTagUsage(tagsData.tagUsage);
    } catch (error) {
      console.error("Error loading data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);
    
    try {
      console.log('Login attempt with:', { username, passwordLength: password.length });
      
      // Use only the secure token-based login method
      const { AUTH_CONFIG } = await import('../config');
      
      console.log('Attempting login...');
      const loginSuccess = await AUTH_CONFIG.login(username, password);
      console.log('Login result:', loginSuccess);
      
      if (loginSuccess) {
        console.log('Login successful, setting authenticated state');
        setIsAuthenticated(true);
        setPassword('');
        setLoginError(null);
      } else {
        console.log('Login failed');
        setLoginError('Invalid username or password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Login failed. Please try again. Error: ' + error.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const { AUTH_CONFIG } = await import('../config');
      AUTH_CONFIG.logout();
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  const handleChangePassword = async () => {
    try {
      if (newPassword !== confirmPassword) {
        setLoginError('New passwords do not match');
        return;
      }
      
      if (newPassword.length < 8) {
        setLoginError('New password must be at least 8 characters');
        return;
      }
      
      // Check for password strength
      const hasLetter = /[a-zA-Z]/.test(newPassword);
      const hasNumber = /[0-9]/.test(newPassword);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
      
      if (!(hasLetter && (hasNumber || hasSpecialChar))) {
        setLoginError('Password must contain letters and at least numbers or special characters');
        return;
      }
      
      const { AUTH_CONFIG } = await import('../config');
      const result = await AUTH_CONFIG.changePassword(currentPassword, newPassword);
      
      if (result.success) {
        // Successfully changed the password 
        if (result.warningMessage) {
          // Show warning about persistence
          showNotification('warning', 'Password changed, but may not persist after restart');
          // Also use a more detailed error
          setLoginError(result.warningMessage);
          
          // Don't close the modal if there's a persistence warning
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        } else {
          // Complete success - close the modal
          showNotification('success', 'Password changed successfully');
          setShowPasswordModal(false);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setLoginError(null);
        }
      } else {
        setLoginError(result.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setLoginError('An error occurred while changing the password: ' + error.message);
    }
  };

  const handleEditQuote = (quoteId) => {
    const quoteToEdit = quotes.find(q => q.id === quoteId);
    if (quoteToEdit) {
      // Format the quote data for the form
      setNewQuote({
        claim_text: quoteToEdit.claim_text,
        claim_source: quoteToEdit.claim_source,
        claim_date: quoteToEdit.claim_date,
        reality_text: quoteToEdit.reality_text || quoteToEdit.claim_text,
        reality_sources: quoteToEdit.reality_sources || '[]',
        veracity_rating: quoteToEdit.veracity_rating || '',
        citation: quoteToEdit.citation || '',
        video_url: quoteToEdit.videoUrl || '',
        tags: quoteToEdit.tags.join(',')
      });
      setEditingQuoteId(quoteId);
      setActiveTab('add');
    }
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    setError(null);
    
    try {
      // DEBUGGING: Add extensive logging for submission flow
      console.log('[QUOTE DEBUG] Starting quote submission process');
      console.log('[QUOTE DEBUG] Quote data structure:', {
        claim_text_length: formData.claim_text?.length || 0,
        claim_source: formData.claim_source,
        claim_date: formData.claim_date,
        reality_text_length: formData.reality_text?.length || 0,
        has_reality_sources: !!formData.reality_sources,
        tags_count: formData.tags?.length || 0,
        has_framework: !!formData.frameworkData,
        framework_pillars: formData.frameworkData?.pillars?.length || 0,
        framework_tactics: formData.frameworkData?.tactics?.length || 0,
        framework_precedents: formData.frameworkData?.precedents?.length || 0
      });
      
      // Log full framework data for debugging
      if (formData.frameworkData) {
        console.log('[QUOTE DEBUG] Framework data details:', {
          pillars: formData.frameworkData.pillars?.map(p => ({
            id: p.id,
            name: p.name,
            impact: p.impact_level,
            justification_length: p.justification?.length || 0
          })) || [],
          tactics: formData.frameworkData.tactics?.map(t => ({
            id: t.id,
            name: t.name,
            justification_length: t.justification?.length || 0
          })) || [],
          precedents: formData.frameworkData.precedents?.map(p => ({
            id: p.id,
            country: p.country,
            relevance_length: p.relevance_note?.length || 0
          })) || []
        });
      }
      
      console.log('[QUOTE DEBUG] Preparing to send data to API...');
      console.log('Submitting quote data:', formData);
      
      // Clear any stored form backup after successful submission
      try {
        localStorage.removeItem('quote_form_backup');
        sessionStorage.removeItem('lastClaudeAnalysis');
      } catch (e) {
        console.warn("Failed to clear form backup:", e);
      }
      
      let response;
      
      // CRITICAL FIX: Use absolute URLs for production domain
      // This ensures we don't have issues with subdomain routing or path resolution
      const isProduction = window.location.hostname.includes('cultoftheredhat.com') || 
                          window.location.hostname.includes('cloudflare');
      // Use the current protocol, hostname, and port to build the absolute URL
      const useDomain = isProduction ? `${window.location.protocol}//${window.location.host}` : '';
      console.log(`[QUOTE DEBUG] Using domain: "${useDomain}" for API requests. Current host: ${window.location.host}`);
      
      let endpoint, method;
      
      if (editingQuoteId) {
        // Update existing quote
        endpoint = `${useDomain}/api/admin/quotes/${editingQuoteId}`;
        method = 'PUT';
        console.log(`[QUOTE DEBUG] Updating existing quote ID: ${editingQuoteId}`);
      } else {
        // Add new quote using the fixed endpoint
        endpoint = `${useDomain}/api/admin/quotes-fix`;
        method = 'POST';
        console.log(`[QUOTE DEBUG] Creating new quote with quotes-fix endpoint: ${endpoint}`);
      }

      console.log(`[QUOTE DEBUG] Fetching ${method} ${endpoint} with data size: ${JSON.stringify(formData).length} bytes`);
      
      const start = performance.now();
      console.log(`[QUOTE DEBUG] Request started at: ${new Date().toISOString()}`);
      
      try {
        // Add new fetch with timeout for better diagnostics
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
        
        // CRITICAL FIX: Try multiple approaches if the main request fails
        let fetchAttempt = 1;
        const maxAttempts = 3;
        
        while (fetchAttempt <= maxAttempts) {
          try {
            console.log(`[QUOTE DEBUG] Fetch attempt ${fetchAttempt}/${maxAttempts} to ${endpoint}`);
            
            // First attempt: standard fetch with credentials
            response = await fetch(endpoint, {
              method: method,
              headers: { 
                'Content-Type': 'application/json',
                'X-Fetch-Attempt': fetchAttempt.toString(),
                'X-Client-Hostname': window.location.hostname
              },
              body: JSON.stringify(formData),
              signal: controller.signal,
              credentials: 'same-origin' // Include cookies for auth if needed
            });
            
            console.log(`[QUOTE DEBUG] Fetch attempt ${fetchAttempt} succeeded with status: ${response.status}`);
            break; // Success, exit the loop
          } catch (fetchErr) {
            console.error(`[QUOTE DEBUG] Fetch attempt ${fetchAttempt} failed:`, fetchErr);
            
            if (fetchAttempt === maxAttempts) {
              // Last attempt failed, rethrow the error
              throw fetchErr;
            }
            
            // If not the last attempt, try different approaches
            if (fetchAttempt === 1) {
              // Second attempt: strip domain and use relative URL
              endpoint = endpoint.replace(/^https?:\/\/[^\/]+/, '');
              console.log(`[QUOTE DEBUG] Next attempt will use relative URL: ${endpoint}`);
            } else if (fetchAttempt === 2) {
              // Third attempt: use fallback endpoint as last resort
              endpoint = endpoint.replace('quotes-fix', 'quotes-fallback');
              console.log(`[QUOTE DEBUG] Last attempt will use fallback endpoint: ${endpoint} (skips framework processing)`);
            }
            
            fetchAttempt++;
            // Wait a short time before retry
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        
        clearTimeout(timeoutId);
        const end = performance.now();
        console.log(`[QUOTE DEBUG] Request completed after ${(end - start).toFixed(2)}ms with status: ${response.status}`);
      } catch (fetchError) {
        console.error(`[QUOTE DEBUG] Fetch error: ${fetchError.message}`);
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timed out after 30 seconds. The server may be overloaded or experiencing issues.');
        }
        throw fetchError;
      }
      
      // First try to parse the response as JSON
      let responseBody;
      try {
        console.log(`[QUOTE DEBUG] Attempting to parse response JSON...`);
        responseBody = await response.json();
        console.log(`[QUOTE DEBUG] Response parsed successfully:`, {
          success: responseBody.success,
          hasError: !!responseBody.error,
          hasDetails: !!responseBody.details,
          hasWarning: !!responseBody.frameworkWarning,
          quoteId: responseBody.quoteId
        });
      } catch (parseError) {
        // If parsing fails, get response as text
        console.error(`[QUOTE DEBUG] Response JSON parse error: ${parseError.message}`);
        console.log(`[QUOTE DEBUG] Attempting to get raw text response...`);
        
        let textResponse;
        try {
          textResponse = await response.text();
          console.log(`[QUOTE DEBUG] Raw response text (first 500 chars): ${textResponse.substring(0, 500)}`);
        } catch (textError) {
          console.error(`[QUOTE DEBUG] Failed to get response text: ${textError.message}`);
          textResponse = "Unable to retrieve response text";
        }
        
        throw new Error(`Server returned invalid JSON. Status: ${response.status}. Response: ${textResponse}`);
      }
      
      // Check if the response is successful
      if (!response.ok) {
        console.error(`[QUOTE DEBUG] Server returned error status: ${response.status}`);
        console.error(`[QUOTE DEBUG] Error details:`, {
          error: responseBody.error,
          details: responseBody.details,
          status: response.status
        });
        throw new Error(responseBody.error || responseBody.details || `Failed with status: ${response.status}`);
      }
      
      // If there was a framework warning, log it but don't throw an error
      if (responseBody.frameworkWarning) {
        console.warn(`[QUOTE DEBUG] Framework warning detected: ${responseBody.frameworkWarning}`);
        console.warn("Quote saved but framework connections had an issue:", responseBody.frameworkWarning);
      }
      
      // Reset the form
      setNewQuote({
        claim_text: '',
        claim_source: '',
        claim_date: new Date().toISOString().split('T')[0],
        reality_text: '',
        reality_sources: '[]',
        veracity_rating: '',
        citation: '',
        video_url: '',
        tags: ''
      });
      
      setEditingQuoteId(null);
      
      // Reload data and check if the new quote was actually added
      await loadData();
      
      console.log(`[QUOTE DEBUG] Checking if quote was added to quotes array (length: ${quotes.length})`);
      
      // Check if the new quote was actually added by looking for it in the quotes array
      const wasQuoteAdded = quotes.some(q => {
        const matchesId = editingQuoteId && q.id === editingQuoteId;
        const matchesContent = !editingQuoteId && 
          q.claim_text === formData.claim_text && 
          q.claim_source === formData.claim_source;
        
        if (matchesId || matchesContent) {
          console.log(`[QUOTE DEBUG] Quote match found in array:`, {
            quote_id: q.id,
            matches_by: matchesId ? 'id' : 'content',
            editing_id: editingQuoteId || 'new'
          });
          return true;
        }
        return false;
      });
      
      if (wasQuoteAdded) {
        console.log(`[QUOTE DEBUG] Quote successfully verified in database after reload`);
        
        if (responseBody.note && responseBody.note.includes('fallback')) {
          // Let the user know we used the fallback endpoint
          console.log(`[QUOTE DEBUG] Quote saved via fallback endpoint`);
          showNotification('success', `Claim added successfully via fallback method. Framework connections were skipped.`);
        } else if (responseBody.frameworkWarning) {
          // Let the user know the quote was saved but framework had issues
          console.log(`[QUOTE DEBUG] Showing framework warning alert to user`);
          showNotification('success', 
            editingQuoteId 
              ? 'Claim updated successfully, but there were issues with framework connections.' 
              : 'Claim added successfully, but there were issues with framework connections.'
          );
        } else {
          console.log(`[QUOTE DEBUG] Showing success alert to user`);
          showNotification('success', editingQuoteId ? 'Claim updated successfully!' : 'Claim added successfully!');
        }
      } else {
        console.error(`[QUOTE DEBUG] Quote was reported as successful but not found after reload`);
        console.log(`[QUOTE DEBUG] Response data:`, responseBody);
        console.log(`[QUOTE DEBUG] Looking for matches with:`, {
          editing_id: editingQuoteId || null,
          claim_text: formData.claim_text?.substring(0, 40) + '...',
          claim_source: formData.claim_source
        });
        
        // Log all quotes for debugging
        console.log(`[QUOTE DEBUG] All quotes in array:`, quotes.map(q => ({
          id: q.id,
          claim_text: q.claim_text?.substring(0, 30) + '...',
          claim_source: q.claim_source
        })));
        
        console.log('Quote was reported as successful but not found after reload. Response:', responseBody);
      }
      
      return responseBody; // Return response for promise resolution
      
    } catch (error) {
      console.error('[QUOTE DEBUG] Error in submission flow:', error);
      console.error('[QUOTE DEBUG] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      console.error('Error saving quote:', error);
      setError(typeof error === 'object' ? error.message : String(error));
      
      // Rethrow to allow QuoteForm to catch and display error
      throw error;
    } finally {
      console.log('[QUOTE DEBUG] Quote submission process complete');
      setLoading(false);
    }
  };

  // Notification toast state
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  
  // Show notification
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 3000);
  };
  
  const handleDeleteQuote = async (quoteId) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`Sending delete request for quote ID: ${quoteId}`);
      
      // Use the main quotes endpoint with better error handling
      const response = await fetch(`/api/admin/quotes/${quoteId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      // Log the raw response for debugging
      console.log(`Delete response status: ${response.status}`);
      
      // Attempt to parse the response
      let responseData;
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      try {
        // Only try to parse as JSON if there's actual content
        if (responseText && responseText.trim()) {
          responseData = JSON.parse(responseText);
        }
      } catch (e) {
        console.error('Failed to parse response as JSON:', e);
        // Continue with error handling using the text response
      }
      
      if (!response.ok) {
        // Construct a detailed error message from available information
        const errorDetail = responseData?.details || responseData?.error || 
                           `Server returned status ${response.status}`;
        throw new Error(errorDetail || 'Failed to delete quote');
      }
      
      // Update local state
      setQuotes(quotes.filter(quote => quote.id !== quoteId));
      setShowDeleteConfirm(null);
      
      // Use toast notification instead of alert
      showNotification('success', 'Claim deleted successfully!');
    } catch (error) {
      console.error('Error deleting quote:', error);
      setError(error.message);
      showNotification('error', `Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingQuoteId(null);
    setNewQuote({
      claim_text: '',
      claim_source: '',
      claim_date: new Date().toISOString().split('T')[0],
      reality_text: '',
      reality_sources: '[]',
      veracity_rating: '',
      citation: '',
      video_url: '',
      tags: ''
    });
  };

  const toggleExpandQuote = (quoteId) => {
    setExpandedQuoteId(expandedQuoteId === quoteId ? null : quoteId);
  };

  const getVeracityBadge = (rating) => {
    if (!rating) return null;
    
    const colors = {
      'False': 'bg-red-100 text-red-800',
      'Misleading': 'bg-orange-100 text-orange-800',
      'Partly True': 'bg-yellow-100 text-yellow-800',
      'Mostly True': 'bg-blue-100 text-blue-800',
      'True': 'bg-green-100 text-green-800'
    };
    
    const colorClass = colors[rating] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {rating}
      </span>
    );
  };

  if (loading && !quotes.length) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading indicator while checking authentication
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  // Only show login form if authentication check is complete and user is not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white shadow-md rounded-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h2 className="mt-4 text-2xl font-bold text-gray-800">Admin Login</h2>
          </div>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <input
                type="text"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                required
              />
              <input
                type="password"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
              />
              <div className="mt-1 text-sm text-gray-500">Enter your admin credentials to log in.</div>
              {loginError && (
                <div className="mt-2 text-sm text-red-600">
                  {loginError}
                </div>
              )}
            </div>
            <button 
              type="submit" 
              className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition duration-200"
              disabled={loginLoading}
            >
              {loginLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging in...
                </span>
              ) : 'Login'}
            </button>
          </form>
          <div className="mt-4 text-center">
            <Link to="/" className="text-blue-500 hover:text-blue-700">
              &larr; Back to claims
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Change Admin Password</h3>
            <div className="space-y-4">
              {loginError && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                  <p className="text-sm">{loginError}</p>
                </div>
              )}
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  id="currentPassword"
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password (min 8 characters)
                </label>
                <input
                  id="newPassword"
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <div className="mt-1 text-sm text-gray-500">
                  Password must contain letters and at least numbers or special characters
                </div>
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <div className="flex space-x-3 mt-5">
                <button
                  type="button"
                  className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  onClick={handleChangePassword}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setLoginError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg flex items-center justify-between
          ${notification.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800'
            : notification.type === 'warning' 
            ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
            : 'bg-red-50 border border-red-200 text-red-800'}`}>
          <div className="flex items-center">
            {notification.type === 'success' 
              ? <CheckCircle className="h-5 w-5 text-green-500 mr-2" /> 
              : notification.type === 'warning'
              ? <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
              : <AlertCircle className="h-5 w-5 text-red-500 mr-2" />}
            <p>{notification.message}</p>
          </div>
          <button 
            onClick={() => setNotification({...notification, show: false})}
            className="ml-4 text-gray-500 hover:text-gray-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Cult of the Red Hat Admin</h1>
          <div className="flex gap-2">
            <button 
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-slate-700 bg-slate-100 hover:bg-slate-200"
            >
              Home
            </button>
            <Link 
              to="/" 
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
            >
              View Site
            </Link>
            <button 
              onClick={() => setShowPasswordModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-yellow-100 hover:bg-yellow-200"
            >
              Change Password
            </button>
            <button 
              onClick={handleLogout}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Logout
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm">Error: {error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white shadow-sm rounded-lg overflow-hidden mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                className={`${
                  activeTab === 'add'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
                onClick={() => setActiveTab('add')}
              >
                {editingQuoteId ? 'Edit Claim' : 'Add New Claim'}
              </button>
              <button
                className={`${
                  activeTab === 'manage'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
                onClick={() => setActiveTab('manage')}
              >
                Manage Claims
              </button>
              <button
                className={`${
                  activeTab === 'tags'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
                onClick={() => setActiveTab('tags')}
              >
                Manage Tags
              </button>
              <button
                className={`${
                  activeTab === 'framework'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
                onClick={() => setActiveTab('framework')}
              >
                Democratic Framework
              </button>
              <button
                className={`${
                  activeTab === 'articles'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
                onClick={() => setActiveTab('articles')}
              >
                Articles
              </button>
              <button
                className={`${
                  activeTab === 'politifact'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } py-4 px-6 font-medium text-sm border-b-2 transition duration-150 ease-in-out focus:outline-none`}
                onClick={() => setActiveTab('politifact')}
              >
                PolitiFact Scraper
              </button>
              <Link 
                to="/admin/policies"
                className="py-4 px-6 font-medium text-sm border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out focus:outline-none"
              >
                Policies
              </Link>
              <Link 
                to="/admin/opinions"
                className="py-4 px-6 font-medium text-sm border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out focus:outline-none"
              >
                Opinions
              </Link>
            </nav>
          </div>

          {activeTab === 'add' && (
            <div className="p-6">
              <div className="mb-4 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">
                  {editingQuoteId ? 'Edit Claim' : 'Add New Claim'}
                </h2>
                {editingQuoteId && (
                  <button 
                    onClick={cancelEdit} 
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
              
              {/* Use the new QuoteForm component */}
              <QuoteForm
                initialQuote={newQuote}
                onSubmit={handleSubmit}
                existingTags={allTags}
                isEditing={!!editingQuoteId}
                onCancel={editingQuoteId ? cancelEdit : null}
              />
            </div>
          )}

          {activeTab === 'manage' && (
            <div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Claim
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rating
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Array.isArray(quotes) ? quotes.map(quote => (
                      <React.Fragment key={quote.id}>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <button 
                                className="text-gray-400 hover:text-gray-500 mr-2"
                                onClick={() => toggleExpandQuote(quote.id)}
                                aria-label={expandedQuoteId === quote.id ? "Collapse claim details" : "Expand claim details"}
                              >
                                {expandedQuoteId === quote.id ? (
                                  <ChevronUp className="h-5 w-5" />
                                ) : (
                                  <ChevronDown className="h-5 w-5" />
                                )}
                              </button>
                              <span className="text-sm text-gray-900 truncate max-w-xs">
                                {quote.claim_text.length > 70 ? quote.claim_text.substring(0, 70) + '...' : quote.claim_text}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{quote.claim_source}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{quote.claim_date}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getVeracityBadge(quote.veracity_rating)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                className="text-blue-600 hover:text-blue-900"
                                onClick={() => handleEditQuote(quote.id)}
                                aria-label="Edit claim"
                              >
                                <Edit className="h-5 w-5" />
                              </button>
                              <button
                                className="text-red-600 hover:text-red-900"
                                onClick={() => setShowDeleteConfirm(quote.id)}
                                aria-label="Delete claim"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                              <button 
                                className="text-indigo-600 hover:text-indigo-900"
                                onClick={() => {
                                  // Use the framework endpoint with history parameter
                                  console.log(`Fetching history for quote ${quote.id}...`);
                                  fetch(`/api/quotes/${quote.id}/framework?history=true`)
                                    .then(res => {
                                      console.log("History fetch response:", res.status, res.statusText);
                                      if (!res.ok) {
                                        throw new Error(`History fetch failed: ${res.status} ${res.statusText}`);
                                      }
                                      return res.json();
                                    })
                                    .then(data => {
                                      console.log("History data received:", data);
                                      
                                      if (data.success && data.analysis_history && data.analysis_history.length > 0) {
                                        const historyText = data.analysis_history.map(item => 
                                          `Model: ${item.model_used}\nTime: ${new Date(item.created_at).toLocaleString()}\n` +
                                          (item.error_reason ? `Error: ${item.error_reason}\n` : '')
                                        ).join('\n---\n\n');
                                        
                                        alert(`Analysis History for Quote #${quote.id}:\n\n${historyText}`);
                                      } else if (data.message) {
                                        alert(`Message from server: ${data.message}`);
                                      } else {
                                        alert('No analysis history found for this quote');
                                      }
                                    })
                                    .catch(err => {
                                      console.error("Error fetching analysis history:", err);
                                      alert(`Error fetching analysis history: ${err.message}`);
                                    });
                                }}
                                aria-label="View analysis history"
                              >
                                <History className="h-5 w-5" />
                              </button>
                              {quote.hasOwnProperty('published') && (
                                <button
                                  className={`text-sm px-2 py-1 rounded ${quote.published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
                                  disabled={quote.published}
                                  onClick={quote.published ? null : () => {
                                    fetch(`/api/admin/quotes/${quote.id}/publish`, {
                                      method: 'POST'
                                    })
                                    .then(response => {
                                      if (!response.ok) throw new Error('Failed to publish quote');
                                      return response.json();
                                    })
                                    .then(() => {
                                      loadData();
                                    })
                                    .catch(error => {
                                      console.error('Error publishing quote:', error);
                                      setError(error.message);
                                    });
                                  }}
                                >
                                  {quote.published ? 'Published' : 'Publish'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expandedQuoteId === quote.id && (
                          <tr className="bg-gray-50">
                            <td colSpan="5" className="px-6 py-4">
                              <div className="space-y-2 text-sm">
                                <div>
                                  <span className="font-medium">Claim:</span> {quote.claim_text}
                                </div>
                                <div>
                                  <span className="font-medium">Reality:</span> {quote.reality_text}
                                </div>
                                {quote.citation && (
                                  <div>
                                    <span className="font-medium">Citation:</span> <a href={quote.citation} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{quote.citation}</a>
                                  </div>
                                )}
                                {quote.videoUrl && (
                                  <div>
                                    <span className="font-medium">Video URL:</span> <a href={quote.videoUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{quote.videoUrl}</a>
                                  </div>
                                )}
                                <div>
                                  <span className="font-medium">Tags:</span> {quote.tags.map((tag, index) => (
                                    <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mr-1">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        {showDeleteConfirm === quote.id && (
                          <tr className="bg-red-50">
                            <td colSpan="5" className="px-6 py-4">
                              <div className="flex justify-between items-center">
                                <div>
                                  <h3 className="text-sm font-medium text-red-800">Are you sure you want to delete this claim?</h3>
                                  <p className="text-xs text-red-700 mt-1">This action cannot be undone.</p>
                                </div>
                                <div className="flex space-x-2">
                                  <button 
                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                    onClick={() => handleDeleteQuote(quote.id)}
                                  >
                                    <Check className="h-4 w-4 mr-1" />
                                    Yes, delete
                                  </button>
                                  <button 
                                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                    onClick={() => setShowDeleteConfirm(null)}
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )) : (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                          No claims available or there was an error loading the data.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => loadData(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center rounded-md border ${
                        currentPage === 1 ? 'border-gray-300 bg-gray-100 text-gray-400' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      } px-4 py-2 text-sm font-medium`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => loadData(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`relative ml-3 inline-flex items-center rounded-md border ${
                        currentPage === totalPages ? 'border-gray-300 bg-gray-100 text-gray-400' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      } px-4 py-2 text-sm font-medium`}
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{quotes.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}</span> to{' '}
                        <span className="font-medium">{Math.min((currentPage) * pageSize, totalQuotes)}</span> of{' '}
                        <span className="font-medium">{totalQuotes}</span> claims
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => loadData(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                            currentPage === 1 ? 'text-gray-300' : 'text-gray-400 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Previous</span>
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                          </svg>
                        </button>
                        
                        {/* Page numbers */}
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          // Calculate the page number to display
                          let pageNum;
                          if (totalPages <= 5) {
                            // If 5 or fewer pages, show all pages
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            // If near start, show first 5 pages
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            // If near end, show last 5 pages
                            pageNum = totalPages - 4 + i;
                          } else {
                            // If in middle, show current page and 2 on each side
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => loadData(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                currentPage === pageNum 
                                  ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600' 
                                  : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => loadData(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                            currentPage === totalPages ? 'text-gray-300' : 'text-gray-400 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Next</span>
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tags' && (
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Manage Tags
              </h2>
              
              {allTags.length === 0 ? (
                <p className="text-gray-500">No tags available yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tag Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Usage
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {allTags.map((tag, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {tag}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {tagUsage[tag] || 0} claims
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex space-x-2">
                              <button
                                className="text-blue-600 hover:text-blue-900"
                                onClick={() => {
                                  // Implement edit tag functionality
                                  const newTagName = prompt(`Edit tag: ${tag}`, tag);
                                  if (newTagName && newTagName !== tag) {
                                    // Call API to update tag
                                    fetch(`/api/admin/tags/${encodeURIComponent(tag)}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ newName: newTagName })
                                    })
                                    .then(response => {
                                      if (!response.ok) throw new Error('Failed to update tag');
                                      return response.json();
                                    })
                                    .then(() => {
                                      loadData();
                                      alert(`Tag "${tag}" renamed to "${newTagName}" successfully!`);
                                    })
                                    .catch(error => {
                                      console.error('Error updating tag:', error);
                                      alert('Failed to update tag: ' + error.message);
                                    });
                                  }
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="text-red-600 hover:text-red-900"
                                onClick={() => {
                                  // Implement delete tag functionality
                                  if (confirm(`Are you sure you want to delete the tag "${tag}"? This will remove it from all claims.`)) {
                                    fetch(`/api/admin/tags/${encodeURIComponent(tag)}`, {
                                      method: 'DELETE'
                                    })
                                    .then(response => {
                                      if (!response.ok) throw new Error('Failed to delete tag');
                                      return response.json();
                                    })
                                    .then(() => {
                                      loadData();
                                      alert(`Tag "${tag}" deleted successfully!`);
                                    })
                                    .catch(error => {
                                      console.error('Error deleting tag:', error);
                                      alert('Failed to delete tag: ' + error.message);
                                    });
                                  }
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              <div className="mt-8">
                <h3 className="text-md font-medium text-gray-900 mb-3">Merge Tags</h3>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="sourceTag" className="block text-sm font-medium text-gray-700 mb-1">
                        Source Tag
                      </label>
                      <select
                        id="sourceTag"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        onChange={(e) => {
                          // Set source tag for merging
                          const selectedTag = e.target.value;
                          if (selectedTag) {
                            // Could store this in state for later use
                          }
                        }}
                      >
                        <option value="">Select a tag to merge</option>
                        {allTags.map((tag, index) => (
                          <option key={index} value={tag}>{tag}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="targetTag" className="block text-sm font-medium text-gray-700 mb-1">
                        Target Tag (merge into this)
                      </label>
                      <select
                        id="targetTag"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select destination tag</option>
                        {allTags.map((tag, index) => (
                          <option key={index} value={tag}>{tag}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={() => {
                      const sourceTag = document.getElementById('sourceTag').value;
                      const targetTag = document.getElementById('targetTag').value;
                      
                      if (!sourceTag || !targetTag) {
                        alert('Please select both source and target tags');
                        return;
                      }
                      
                      if (sourceTag === targetTag) {
                        alert('Source and target tags cannot be the same');
                        return;
                      }
                      
                      if (confirm(`Are you sure you want to merge tag "${sourceTag}" into "${targetTag}"? This cannot be undone.`)) {
                        fetch('/api/admin/tags/merge', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ sourceTag, targetTag })
                        })
                        .then(response => {
                          if (!response.ok) throw new Error('Failed to merge tags');
                          return response.json();
                        })
                        .then(() => {
                          loadData();
                          alert(`Tag "${sourceTag}" merged into "${targetTag}" successfully!`);
                        })
                        .catch(error => {
                          console.error('Error merging tags:', error);
                          alert('Failed to merge tags: ' + error.message);
                        });
                      }
                    }}
                  >
                    Merge Tags
                  </button>
                </div>
              </div>
              
              <div className="mt-8">
                <h3 className="text-md font-medium text-gray-900 mb-3">Add New Tag</h3>
                <div className="p-4 bg-gray-50 rounded-lg flex space-x-2">
                  <input
                    type="text"
                    id="newTagName"
                    className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter new tag name"
                  />
                  <button
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={() => {
                      const newTagName = document.getElementById('newTagName').value.trim();
                      
                      if (!newTagName) {
                        alert('Please enter a tag name');
                        return;
                      }
                      
                      if (allTags.includes(newTagName)) {
                        alert('This tag already exists');
                        return;
                      }
                      
                      // We can create the tag by "updating" a non-existent tag
                      // This is a bit of a hack, but it will work for our purposes
                      const tempTag = "____NEW_TAG____";
                      fetch(`/api/admin/tags/${encodeURIComponent(tempTag)}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ newName: newTagName })
                      })
                      .then(response => {
                        if (response.status === 404) {
                          // Tag not found, create it
                          return fetch('/api/admin/quotes', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              claim_text: `Temporary claim for creating tag "${newTagName}"`,
                              claim_source: "System",
                              claim_date: new Date().toISOString().split('T')[0],
                              reality_text: "This is a temporary claim that will be deleted.",
                              tags: [newTagName]
                            })
                          });
                        }
                        return response;
                      })
                      .then(response => {
                        if (!response.ok) throw new Error('Failed to create tag');
                        return response.json();
                      })
                      .then(() => {
                        document.getElementById('newTagName').value = '';
                        loadData();
                        alert(`Tag "${newTagName}" created successfully!`);
                      })
                      .catch(error => {
                        console.error('Error creating tag:', error);
                        alert('Failed to create tag: ' + error.message);
                      });
                    }}
                  >
                    Add Tag
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Democratic Framework Tab */}
          {activeTab === 'framework' && (
            <DemocraticFrameworkTab />
          )}

          {/* PolitiFact Scraper Tab */}
          {activeTab === 'politifact' && (
            <PolitiFactScraper />
          )}

          {/* Articles Tab */}
          {activeTab === 'articles' && (
            <ArticleAdminTab />
          )}

          {/* No database tab section anymore */}
        </div>
      </div>
    </div>
  );
};

export default Admin;